import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ClipboardList, Plus, CheckCircle2, Edit, Trash2, Clock, AlertCircle } from 'lucide-react';
import { useTasks } from '@/hooks/queries';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { TaskCadence } from '@/types/tasks';

const taskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  cadence: z.enum(['once', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'custom_rrule']),
  rrule: z.string().optional(),
  owner: z.string().optional(),
  due_date: z.string().min(1, 'Due date is required'),
});

type TaskFormData = z.infer<typeof taskSchema>;

const statusColors = {
  Scheduled: 'bg-secondary text-secondary-foreground',
  Due: 'bg-warning text-warning-foreground',
  Overdue: 'bg-destructive text-destructive-foreground',
  Done: 'bg-compliant text-compliant-foreground',
  Skipped: 'bg-muted text-muted-foreground',
};

export default function TasksPage() {
  const { organization } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const {
    tasks,
    isLoading,
    createTask,
    updateTask,
    deleteTask,
    completeTask,
    isCreating,
    isUpdating,
    isDeleting,
  } = useTasks(organization?.id);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      cadence: 'monthly',
      due_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
    },
  });

  const watchedCadence = watch('cadence');
  const selectedTask = tasks?.find(t => t.id === selectedTaskId);

  const filteredTasks = tasks?.filter(task => {
    if (statusFilter === 'all') return true;
    return task.status === statusFilter;
  }) || [];

  const overdueCount = tasks?.filter(t => t.status === 'Overdue').length || 0;
  const dueCount = tasks?.filter(t => t.status === 'Due').length || 0;
  const scheduledCount = tasks?.filter(t => t.status === 'Scheduled').length || 0;
  const doneCount = tasks?.filter(t => t.status === 'Done').length || 0;

  const handleCreate = (data: TaskFormData) => {
    if (!organization?.id) return;
    
    createTask({
      title: data.title,
      description: data.description || '',
      cadence: data.cadence,
      rrule: data.rrule,
      owner: data.owner,
      due_next: dayjs(data.due_date).toISOString(),
      status: 'Scheduled',
      org_id: organization.id,
    });
    
    reset();
    setCreateDialogOpen(false);
  };

  const handleEdit = (data: TaskFormData) => {
    if (!selectedTaskId) return;

    updateTask({
      id: selectedTaskId,
      data: {
        title: data.title,
        description: data.description,
        cadence: data.cadence,
        rrule: data.rrule,
        owner: data.owner,
        due_next: dayjs(data.due_date).toISOString(),
      },
    });

    reset();
    setEditDialogOpen(false);
    setSelectedTaskId(null);
  };

  const handleDelete = () => {
    if (!selectedTaskId) return;
    deleteTask(selectedTaskId);
    setDeleteDialogOpen(false);
    setSelectedTaskId(null);
  };

  const handleComplete = (taskId: string) => {
    completeTask(taskId);
  };

  const openEditDialog = (taskId: string) => {
    const task = tasks?.find(t => t.id === taskId);
    if (!task) return;

    reset({
      title: task.title,
      description: task.description || '',
      cadence: task.cadence as TaskCadence,
      rrule: task.rrule || '',
      owner: task.owner || '',
      due_date: dayjs(task.due_next).format('YYYY-MM-DD'),
    });

    setSelectedTaskId(taskId);
    setEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Tasks</h1>
            <p className="text-muted-foreground">Manage recurring tasks and maintenance schedules</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tasks</h1>
          <p className="text-muted-foreground">Manage recurring tasks and maintenance schedules</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Task
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overdueCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due Soon</CardTitle>
            <Clock className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dueCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Scheduled</CardTitle>
            <ClipboardList className="h-4 w-4 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{scheduledCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-compliant" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{doneCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Overdue">Overdue</SelectItem>
            <SelectItem value="Due">Due</SelectItem>
            <SelectItem value="Scheduled">Scheduled</SelectItem>
            <SelectItem value="Done">Done</SelectItem>
            <SelectItem value="Skipped">Skipped</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-10">
              <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No tasks found</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Task
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredTasks.map((task) => (
            <Card key={task.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{task.title}</CardTitle>
                      <Badge className={statusColors[task.status]}>{task.status}</Badge>
                      <Badge variant="outline">{task.cadence}</Badge>
                    </div>
                    {task.description && (
                      <CardDescription>{task.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {task.status !== 'Done' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleComplete(task.id)}
                      >
                        <CheckCircle2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(task.id)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedTaskId(task.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-medium">{dayjs(task.due_next).format('MMM D, YYYY')}</p>
                  </div>
                  {task.owner && (
                    <div>
                      <p className="text-muted-foreground">Owner</p>
                      <p className="font-medium">{task.owner}</p>
                    </div>
                  )}
                  {task.related_to_type && task.related_to_id && (
                    <div>
                      <p className="text-muted-foreground">Related To</p>
                      <p className="font-medium capitalize">{task.related_to_type}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) {
          reset();
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
          setSelectedTaskId(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editDialogOpen ? 'Edit Task' : 'Create Task'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit(editDialogOpen ? handleEdit : handleCreate)} className="space-y-4">
            <div>
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                {...register('title')}
                placeholder="Enter task title"
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Describe the task"
                className="min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cadence">Cadence *</Label>
                <Select
                  value={watchedCadence}
                  onValueChange={(value) => setValue('cadence', value as TaskCadence)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Once</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="custom_rrule">Custom (RRULE)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="due_date">Next Due Date *</Label>
                <Input
                  id="due_date"
                  type="date"
                  {...register('due_date')}
                />
                {errors.due_date && (
                  <p className="text-sm text-destructive">{errors.due_date.message}</p>
                )}
              </div>
            </div>

            {watchedCadence === 'custom_rrule' && (
              <div>
                <Label htmlFor="rrule">RRULE</Label>
                <Input
                  id="rrule"
                  {...register('rrule')}
                  placeholder="FREQ=MONTHLY;INTERVAL=1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter a valid RRULE string for custom recurrence patterns
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="owner">Owner</Label>
              <Input
                id="owner"
                {...register('owner')}
                placeholder="Person or team responsible"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => {
                reset();
                setCreateDialogOpen(false);
                setEditDialogOpen(false);
                setSelectedTaskId(null);
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating}>
                {editDialogOpen ? 'Update Task' : 'Create Task'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTask?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
